import * as React from 'react'
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tabs,
  Tab,
  TextField,
  Select,
  MenuItem,
  Button,
  Typography,
  Divider,
  InputLabel,
  FormControl,
} from '@mui/material'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import EditOutlinedIcon from '@assets/Analytics/Pen.svg'
import 'dayjs/locale/he'
import ConfirmDeleteDialog from './ConfirmDeleteDialog'
import DeleteActionButton from './DeleteActionButton'
import { WaterTreeZoneType } from '@/services/dashboardService'
import { useAppTheme } from '@/theme/ThemeContext'

type CreatePayload = {
  name: string
  zoneTypeId: number | null
  copyFromId?: number | null
}
type UpdatePayload = { name: string; zoneTypeId: number | null }

type Props = {
  open: boolean
  mode?: 'edit' | 'create'
  initial?: { name?: string; zoneTypeId?: number | null; copyFromId?: number }
  zoneId?: number
  onClose: () => void
  onCreate?: (data: CreatePayload) => void
  onUpdate?: (data: UpdatePayload) => void
  onDelete?: () => void
  zoneTypes?: WaterTreeZoneType[]
  copyOptions?: { id: number; name: string }[]
}

type Form = {
  name: string
  zoneTypeId: number | null
  copyFromId: number | ''
}

const WaterTreeEditorDialog: React.FC<Props> = ({
  open,
  mode = 'edit',
  initial,
  onClose,
  onCreate,
  onUpdate,
  onDelete,
  zoneTypes = [],
  copyOptions = [],
}) => {
  const { mode: appMode } = useAppTheme()
  const isDarkMode = appMode === 'dark'
  
  const themeColors = {
    bg: isDarkMode ? '#202B40' : '#FFFFFF',
    text: isDarkMode ? '#FFFFFF' : '#003F61',
    textSecondary: isDarkMode ? '#BBD7FF' : '#003F61',
    border: isDarkMode ? '#4B6FB1' : '#E6ECF2',
    tabInactive: isDarkMode ? '#BBD7FF80' : '#8A9DB0',
    saveBg: isDarkMode ? '#003089' : '#E3F5FF',
    saveText: isDarkMode ? '#FFFFFF' : '#003089',
    saveHover: isDarkMode ? '#00256b' : '#D0EEFF',
  }

  const [tab, setTab] = React.useState<'edit' | 'create'>(mode)
  const [confirmOpen, setConfirmOpen] = React.useState(false)
  const [showErrors, setShowErrors] = React.useState(false)
  const [saving, setSaving] = React.useState(false)

  const [form, setForm] = React.useState<Form>({
    name: initial?.name ?? '',
    zoneTypeId: initial?.zoneTypeId ?? null,
    copyFromId: initial?.copyFromId ?? '',
  })

  React.useEffect(() => { setTab(mode) }, [mode])

  React.useEffect(() => {
    if (open) {
      setForm({
        name: initial?.name ?? '',
        zoneTypeId: initial?.zoneTypeId ?? null,
        copyFromId: initial?.copyFromId ?? '',
      })
      setShowErrors(false)
    }
  }, [open, initial])

  const set = <K extends keyof Form>(key: K) => (val: Form[K]) =>
    setForm(prev => ({ ...prev, [key]: val }))

  const nameError = showErrors && !form.name.trim()

  const handleSave = async () => {
    // Same save rule as before — but a rejected save is now VISIBLE
    // (error + "שדה חובה" on the offending fields) instead of a silent return.
    if (!form.name.trim()) {
      setShowErrors(true)
      return
    }
    setSaving(true)
    try {
      if (tab === 'create') {
        await onCreate?.({
          name: form.name.trim(),
          zoneTypeId: form.zoneTypeId,
          copyFromId: form.copyFromId !== '' ? (form.copyFromId as number) : null,
        })
      } else {
        await onUpdate?.({
          name: form.name.trim(),
          zoneTypeId: form.zoneTypeId,
        })
      }
    } finally {
      setSaving(false)
    }
  }

  const handleConfirmDelete = () => {
    setConfirmOpen(false)
    onDelete?.()
    onClose()
  }

  const rtlInputSx = {
    '& .MuiInputLabel-root': {
      left: 'auto',
      right: 28,
      transformOrigin: 'top right',
      color: themeColors.textSecondary,
    },
    '& .MuiInputLabel-shrink': {
      transform: 'translate(14px, -9px) scale(0.75) !important',
    },
    '& .MuiOutlinedInput-root': {
      color: themeColors.text,
      '& fieldset': { borderColor: themeColors.border },
      '&:hover fieldset': { borderColor: isDarkMode ? '#BBD7FF' : '#003F61' },
      '&.Mui-focused fieldset': { borderColor: isDarkMode ? '#BBD7FF' : '#003F61' },
    },
    '& .MuiOutlinedInput-notchedOutline': { textAlign: 'right' },
    '& .MuiSelect-icon': { left: 7, right: 'auto', color: themeColors.textSecondary },
    '& .MuiSelect-select': {
      paddingLeft: '32px !important',
      paddingRight: '14px !important',
    },
    '& .MuiInputBase-input': { textAlign: 'right' },
  }

  const selectMenuProps = {
    disablePortal: true,
    anchorOrigin: { vertical: 'bottom' as const, horizontal: 'right' as const },
    transformOrigin: { vertical: 'top' as const, horizontal: 'right' as const },
    PaperProps: {
      sx: {
        bgcolor: themeColors.bg,
        color: themeColors.text,
        height: 'fit-content !important',
        minHeight: '0 !important',
        maxHeight: '280px !important',
        minWidth: '150px !important',
        overflowY: 'auto',
        '& .MuiList-root': {
          height: 'fit-content !important',
          minHeight: '0 !important',
          paddingTop: '4px',
          paddingBottom: '4px',
        },
        '& .MuiMenuItem-root': { minHeight: 40 },
      },
    },
    MenuListProps: { sx: { height: 'auto', minHeight: 0, py: 0.5 } },
  }

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="md"
        dir="rtl"
        slotProps={{
          backdrop: {
            sx: {
              backdropFilter: 'blur(4px)',
              backgroundColor: 'rgba(0, 0, 0, 0.2)',
            },
          },
        }}
        PaperProps={{ sx: { borderRadius: '12px', bgcolor: themeColors.bg, color: themeColors.text, boxShadow: '0 8px 32px rgba(0,0,0,0.1)' } }}
      >
        <DialogTitle sx={{ pt: 4, pb: 0, bgcolor: themeColors.bg }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box component="img" src={EditOutlinedIcon} alt="edit" sx={{ width: 22, filter: isDarkMode ? 'brightness(0) invert(1)' : 'brightness(0) saturate(100%) invert(84%) sepia(12%) saturate(1257%) hue-rotate(34deg) brightness(97%) contrast(85%)' }} />
              <Typography sx={{ fontWeight: 400, color: themeColors.textSecondary, fontSize: '18px', fontFamily: 'Rubik, sans-serif' }}>
                עריכת ויצירת עץ מים
              </Typography>
            </Box>
            <IconButton onClick={onClose} size="small" sx={{ color: themeColors.textSecondary }}>
              <CloseRoundedIcon />
            </IconButton>
          </Box>

          <Divider sx={{ borderColor: themeColors.border, mb: 2 }} />

          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            sx={{
              '& .MuiTab-root': { color: themeColors.tabInactive, fontWeight: 500, fontFamily: 'Rubik, sans-serif', fontSize: '16px' },
              '& .MuiTab-root.Mui-selected': { color: themeColors.textSecondary, fontWeight: 600 },
            }}
            TabIndicatorProps={{ sx: { backgroundColor: themeColors.textSecondary, height: 2, borderRadius: '4px 4px 0 0' } }}
          >
            <Tab value="edit" label="עריכה" />
            <Tab value="create" label="יצירה" />
          </Tabs>
          <Divider sx={{ mt: 0, borderColor: themeColors.border }} />
        </DialogTitle>

        <DialogContent sx={{ minHeight: '200px', bgcolor: themeColors.bg }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 2, alignItems: 'flex-start', width: '100%' }}>

            <Box sx={{ width: 150 }}>
              <TextField
                label="שם"
                fullWidth
                size="small"
                value={form.name}
                onChange={e => set('name')(e.target.value)}
                InputLabelProps={{ shrink: true }}
                error={nameError}
                helperText={nameError ? 'שדה חובה' : undefined}
                sx={rtlInputSx}
              />
            </Box>

            <Box sx={{ width: 150 }}>
              <FormControl fullWidth size="small" sx={rtlInputSx}>
                <InputLabel id="kind-label" shrink>סוג</InputLabel>
                <Select<number | ''>
                  labelId="kind-label"
                  label="סוג"
                  displayEmpty
                  value={form.zoneTypeId ?? ''}
                  onChange={e =>
                    set('zoneTypeId')(
                      e.target.value === '' ? null : e.target.value
                    )
                  }
                  MenuProps={selectMenuProps}
                >
                  <MenuItem value="">ללא סוג</MenuItem>
                  {zoneTypes.map(zt => (
                    <MenuItem key={zt.id} value={zt.id}>{zt.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Box sx={{ width: 150 }}>
              <FormControl fullWidth size="small" sx={rtlInputSx}>
                <InputLabel id="copy-label" shrink>העתקת עץ מים</InputLabel>
                <Select
                  labelId="copy-label"
                  label="העתקת עץ מים"
                  displayEmpty
                  value={form.copyFromId}
                  onChange={e => set('copyFromId')(e.target.value as number)}
                  MenuProps={selectMenuProps}
                >
                  <MenuItem value=""><em>—</em></MenuItem>
                  {copyOptions.map(opt => (
                    <MenuItem key={opt.id} value={opt.id}>{opt.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>
        </DialogContent>

        <Divider sx={{ borderColor: themeColors.border, mx: 2 }} />

        <DialogActions sx={{ px: 2, pb: 3, pt: 2, justifyContent: 'flex-end', gap: 2, bgcolor: themeColors.bg }}>
          {tab === 'edit' && (
            <DeleteActionButton onClick={() => setConfirmOpen(true)}>
              מחיקת עץ המים
            </DeleteActionButton>
          )}
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || !form.name.trim()}
            sx={{
              bgcolor: themeColors.saveBg,
              color: themeColors.saveText,
              boxShadow: 'none',
              borderRadius: '6px',
              px: 4,
              py: 0.8,
              fontSize: '14px',
              fontFamily: 'Rubik, sans-serif',
              fontWeight: 500,
              '&:hover': { bgcolor: themeColors.saveHover, boxShadow: 'none' },
            }}
          >
            {saving ? 'שומר...' : 'שמור'}
          </Button>
        </DialogActions>

        <ConfirmDeleteDialog
          open={confirmOpen}
          onClose={() => setConfirmOpen(false)}
          onConfirm={handleConfirmDelete}
          title="למחוק את עץ המים?"
          content="עץ המים וכל השיוכים של המדים אליו יימחקו, ולא יהיה ניתן לשחזר אותו."
        />
      </Dialog>
    </>
  )
}

export default WaterTreeEditorDialog
